import { useTranslation } from 'react-i18next'
import cx from 'helpers/cx'
import sortByProperty from 'helpers/sortByProperty'

import { CrossIcon } from '@pleio/icons/cross'
import { MoveVerticalIcon } from '@pleio/icons/move-vertical'

import { AccessIcon, useAccessTitle } from 'js/components/AccessIcon/AccessIcon'
import DropdownButton from 'js/components/DropdownButton'
import Flexer from 'js/components/Flexer/Flexer'
import IconButton from 'js/components/IconButton'
import Select from 'js/components/Select/Select'
import Textfield from 'js/components/Textfield/Textfield'
import { useNavigationPluginOptions } from 'js/group/AddEditForm/hooks/usePlugins'

import styles from './NavigationMenuItem.module.css'

const NavigationMenuItem = ({
    id,
    dragHandleProps,
    entity,
    groupPageOptions,
    onChangePage,
    onChangeLabel,
    onChangeAccess,
    onRemove,
    ...rest
}) => {
    const { t } = useTranslation()
    const { className, ...domRest } = rest

    const { type, plugin, access } = entity

    const isStartPage = id === 'startPage'

    const pluginOptions = useNavigationPluginOptions().sort(
        sortByProperty('label'),
    )

    const visibilityOptions = [
        {
            onClick: () => onChangeAccess('public'),
            label: t('permissions.public'),
            active: access === 'public',
        },
        {
            onClick: () => onChangeAccess('loggedIn'),
            label: t('permissions.logged-in-users'),
            active: access === 'loggedIn',
        },
        {
            onClick: () => onChangeAccess('members'),
            label: t('permissions.members'),
            active: access === 'members',
        },
    ]

    const currentVisibilityLevel = useAccessTitle(access)

    const typeOptions = [
        ...(groupPageOptions?.length > 0
            ? [
                  {
                      value: 'page',
                      label: t('page.name', { count: 1 }),
                  },
              ]
            : []),
        {
            value: 'plugin',
            label: t('entity-group.template'),
        },
        {
            value: 'link',
            label: t('form.link'),
        },
    ]

    return (
        <div className={cx(styles.wrapper, className)} {...domRest}>
            {!!dragHandleProps && (
                <IconButton
                    as="div"
                    size="large"
                    variant="secondary"
                    {...dragHandleProps}
                >
                    <MoveVerticalIcon />
                </IconButton>
            )}

            <div className="LinkItemFlexer">
                {entity.type === 'submenu' ? (
                    <Textfield
                        name={`${id}-name`}
                        label={t('form.title')}
                        value={entity.label}
                        required
                        onChange={(evt) => onChangeLabel(evt.target.value)}
                    />
                ) : (
                    <>
                        {!isStartPage && (
                            <Select
                                name={`${id}-type`}
                                label={t('global.type')}
                                options={typeOptions}
                                borderStyle="rounded-left"
                                value={entity.type}
                                onChange={(type) =>
                                    onChangePage({
                                        type,
                                        id:
                                            type === 'page'
                                                ? groupPageOptions[0].value
                                                : type === 'plugin'
                                                  ? pluginOptions[0].value
                                                  : null,
                                        access,
                                    })
                                }
                                style={{ maxWidth: '120px' }}
                            />
                        )}

                        {/* Type: page */}
                        {(type === 'page' || isStartPage) && (
                            <>
                                {/* No access */}
                                {entity.id && entity.page === null ? (
                                    <Select
                                        name={`page-${id}`}
                                        label={t('page.name', { count: 1 })}
                                        options={[
                                            {
                                                value: entity.id,
                                                label: t(
                                                    'error.no-access-to-this-page',
                                                ),
                                            },
                                        ]}
                                        value={entity.id}
                                        borderStyle="rounded-right"
                                        disabled
                                    />
                                ) : (
                                    <Select
                                        name={`page-${id}`}
                                        label={t('page.name', { count: 1 })}
                                        options={groupPageOptions}
                                        value={entity.page?.guid || entity.id}
                                        borderStyle="rounded-right"
                                        onChange={(id) =>
                                            onChangePage({
                                                id,
                                                type: 'page',
                                                access,
                                            })
                                        }
                                    />
                                )}
                            </>
                        )}

                        {/* Type: plugin */}
                        {type === 'plugin' && (
                            <Select
                                name={`plugin-${id}`}
                                label={t('entity-group.template')}
                                options={pluginOptions}
                                value={plugin || entity.id}
                                borderStyle={
                                    isStartPage ? 'rounded' : 'rounded-right'
                                }
                                onChange={(id) =>
                                    onChangePage({
                                        id,
                                        type: 'plugin',
                                        access,
                                    })
                                }
                            />
                        )}

                        {/* Type: link */}
                        {type === 'link' && (
                            <>
                                <Textfield
                                    name={`${id}-label`}
                                    label={t('form.title')}
                                    value={entity.label}
                                    onChange={(evt) =>
                                        onChangePage({
                                            type: 'link',
                                            label: evt.target.value,
                                            link: entity.link,
                                            id: entity.id,
                                            access,
                                        })
                                    }
                                    borderStyle="square"
                                    style={{
                                        marginLeft: '-1px',
                                        marginRight: '-1px',
                                    }}
                                />
                                <Textfield
                                    name={`${id}-link`}
                                    label={t('form.link')}
                                    value={entity.link}
                                    onChange={(evt) =>
                                        onChangePage({
                                            type: 'link',
                                            label: entity.label,
                                            link: evt.target.value,
                                            id: evt.target.value,
                                            access,
                                        })
                                    }
                                    borderStyle="rounded-right"
                                />
                            </>
                        )}
                    </>
                )}
            </div>

            <Flexer gutterSize="md" divider dividerOffset="sm">
                {onChangeAccess && (
                    <DropdownButton
                        options={visibilityOptions}
                        side="bottom"
                        align="start"
                    >
                        <IconButton
                            variant="secondary"
                            size="large"
                            tooltip={currentVisibilityLevel}
                        >
                            <AccessIcon access={access} />
                        </IconButton>
                    </DropdownButton>
                )}

                {onRemove && (
                    <IconButton
                        size="large"
                        variant="secondary"
                        onClick={onRemove}
                    >
                        <CrossIcon />
                    </IconButton>
                )}
            </Flexer>
        </div>
    )
}

export default NavigationMenuItem
